import React, { useCallback, useMemo, useState } from 'react';

import { Loading } from '@/components/shared/Loading/Loading';
import { NoResults } from '@/components/shared/NoResults/NoResults';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useContentViewStore } from '@/lib/store/contentViewStore';

import { useContentQueries } from '../services/ContentManagerService';
import BlockCard from './BlockList/BlockCard';
import BlockFilters, { GetBlockParams } from './BlockList/BlockFilters';
import { useFilteredSortedBlocks } from './hooks/useFilteredSortedBlocks';
import { sortBlocksByPosition } from './utils/sortBlocksByPosition';

interface CollectionBlocksViewProps {
  collectionSlug: string;
  onBack: () => void;
  onDeleteBlock: (slug: string) => void;
  onCopyBlock: (slug: string) => void;
}

const CollectionBlocksView: React.FC<CollectionBlocksViewProps> = ({
  collectionSlug,
  onBack,
  onDeleteBlock,
  onCopyBlock,
}) => {
  const { useCollectionDetailsWithAllBlocks, useUpdateCollectionBlockPosition } =
    useContentQueries();
  const { setEditingSlug, setDraftType, setViewMode, setEditorReturnView } = useContentViewStore();
  const filters = useContentViewStore((state) => state.collectionBlockFilters);
  const setFilters = useContentViewStore((state) => state.setCollectionBlockFilters);

  const debouncedTitle = useDebounce(filters.title, 300);
  const queryFilters = useMemo(
    () => ({
      ...filters,
      title: debouncedTitle,
    }),
    [filters, debouncedTitle],
  );

  const { data, isLoading, isError } = useCollectionDetailsWithAllBlocks(collectionSlug);

  // Moving a block swaps it with its visible neighbour, which a filter can hide, so reordering
  // only makes sense on the full list.
  const isFiltered = !!(
    queryFilters.title ||
    queryFilters.type?.length ||
    queryFilters.language ||
    queryFilters.status?.length
  );

  const { orderedBlocks, setOrderedBlocks } = useFilteredSortedBlocks(
    data?.blocks?.items,
    queryFilters,
  );

  const handleFilterChange = (
    key: keyof GetBlockParams,
    value: GetBlockParams[keyof GetBlockParams],
  ) => {
    setFilters({ ...filters, [key]: value });
  };

  const updatePositionMutation = useUpdateCollectionBlockPosition();
  const [isReordering, setIsReordering] = useState(false);

  const swapPositions = useCallback(
    async (fromIndex: number, toIndex: number) => {
      if (isReordering) return;

      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= orderedBlocks.length ||
        toIndex >= orderedBlocks.length
      ) {
        return;
      }

      const fromBlock = orderedBlocks[fromIndex];
      const toBlock = orderedBlocks[toIndex];
      if (!fromBlock || !toBlock) return;

      let fromPos = fromBlock.position ?? fromIndex + 1;
      let toPos = toBlock.position ?? toIndex + 1;

      if (fromPos === toPos) {
        fromPos = fromIndex + 1;
        toPos = toIndex + 1;
      }

      setOrderedBlocks((prev) => {
        const updated = prev.map((b, idx) => {
          if (idx === fromIndex) return { ...b, position: toPos };
          if (idx === toIndex) return { ...b, position: fromPos };
          return b;
        });
        return sortBlocksByPosition(updated);
      });

      setIsReordering(true);
      try {
        await updatePositionMutation.mutateAsync({
          collectionSlug,
          blockSlug: fromBlock.slug,
          position: toPos,
        });
        await updatePositionMutation.mutateAsync({
          collectionSlug,
          blockSlug: toBlock.slug,
          position: fromPos,
        });
      } finally {
        setIsReordering(false);
      }
    },
    [collectionSlug, orderedBlocks, updatePositionMutation, isReordering, setOrderedBlocks],
  );

  const handleEditBlock = useCallback(
    (slug: string) => {
      setEditingSlug(slug);
      setDraftType(undefined);
      setEditorReturnView('collectionBlocks');
      setViewMode('editor');
    },
    [setEditingSlug, setDraftType, setEditorReturnView, setViewMode],
  );

  const content = useMemo(() => {
    if (isLoading) {
      return <Loading />;
    }
    if (isError) {
      return <NoResults />;
    }
    return orderedBlocks.map((block, index) => (
      <BlockCard
        onEditBlock={handleEditBlock}
        onDeleteBlock={onDeleteBlock}
        onCopyBlock={onCopyBlock}
        key={block.slug}
        block={{
          ...block,
          showFeedback: false,
        }}
        canMoveUp={index > 0 && !isReordering}
        canMoveDown={index < orderedBlocks.length - 1 && !isReordering}
        onMoveUp={() => swapPositions(index, index - 1)}
        onMoveDown={() => swapPositions(index, index + 1)}
        shouldShowMoveButtons={!isFiltered}
        isReordering={isReordering}
      />
    ));
  }, [
    isLoading,
    isError,
    orderedBlocks,
    handleEditBlock,
    onDeleteBlock,
    onCopyBlock,
    swapPositions,
    isReordering,
    isFiltered,
  ]);

  return (
    <div className="grid gap-4">
      <div className="flex justify-between items-center">
        <button
          className="text-2xl font-semibold text-neutral-90 flex items-center gap-4"
          onClick={onBack}
        >
          <img src="/icons/arrow-left.svg" alt="back" /> {data?.title}
        </button>
      </div>

      <BlockFilters filters={filters} onFilterChange={handleFilterChange} />

      {!orderedBlocks.length && <NoResults />}
      {content}
    </div>
  );
};

export default CollectionBlocksView;
